<template>
  <div>
    <div class="my-6">
      <h3 class="font-bold">
        Create a new {{ route.name.split("/")[0].slice(0, -1).toLowerCase() }}
      </h3>
      <span class="text-sm text-gray-500">Fill the following information and click the <b>create</b> button to
        save changes.
      </span>
    </div>

    <div class="card shadow-sm bg-white border p-8">
      <span class="lg:inline-grid w-full grid-cols-2 gap-x-4 gap-y-4 mb-6">
        <div class="flex-1 mr-3">
          <Input v-model="data.name" type="default" label="Name" placeholder="Project Name" />
        </div>
        <div class="flex-1 mr-3">
          <Input v-model="data.sprint" type="default" label="Business" placeholder="Business" />
        </div>

        <div class="flex-1 mr-3">
          <Input v-model="data.status" type="select" label="Status" placeholder="Status" :data="status.map((elemento) => {
            return {
              label: elemento.name,
              value: elemento.id,
            };
          })
            " />
        </div>

        <div class="flex-1 mr-3">
          <Input v-model="data.start" type="date" label="Start Date" placeholder="Start Date" />
        </div>

        <div class="flex-1 mr-3">
          <Input v-model="data.finish" type="date" label="Finish Date" placeholder="Finish Date" />
        </div>


        <Input v-model="data.required_hours" type="default" label="Required Hours" placeholder="Hours" />
        <div class="flex-1 mr-3">
          <Input v-model="data.client" type="default" label="Client Account" placeholder="Client Account" />
        </div>
        <div class="flex-1 mr-3">
          <Input v-model="data.manager" type="select" label="Manager" placeholder="Manager" :data="managers.map((elemento) => {
            return {
              label: elemento.name,
              value: elemento.id,
            };
          })
            " />
        </div>
        <div class="flex-1 mr-3">
          <Input v-model="data.coordenator" type="select" label="Coordinator" placeholder="Coordinator" :data="coordenators.map((elemento) => {
            return {
              label: elemento.name,
              value: elemento.id,
            };
          })
            " />
        </div>

        <div class="flex-1 mr-3">
          <Input v-model="data.teamleader" type="select" label="TeamLeader" placeholder="Team Leader" :data="teamleaders.map((elemento) => {
            return {
              label: elemento.name,
              value: elemento.id,
            };
          })
            " />
        </div>

        <div class="flex-1 mr-3">
          <Input v-model="data.clientId" type="select" label="Client" placeholder="Client" :data="clients.map((elemento) => {
            return {
              label: elemento.name,
              value: elemento.id,
            };
          })
            " />
        </div>

        <div class="flex-1 mr-3">
          <Input v-model="data.sector" type="default" label="Sector" placeholder="Sector" />
        </div>
      </span>

      <div class="border rounded-lg bg-white p-6 mb-6">
        <h4 class="font-bold text-gray-700 mb-4">Team</h4>

        <div class="w-full flex flex-col gap-y-6 mt-6">
          <div
            v-for="(group, groupName) in groupedUsers"
            :key="groupName"
            class="flex flex-col relative auto border rounded-lg bg-white appearance-none py-4"
          >
            <span class="text-gray-700 bg-white px-3 -top-2 left-4 absolute text-sm font-bold capitalize">
              {{ groupName }}
            </span>

            <div class="flex flex-wrap gap-x-4 gap-y-2 px-4 mt-2">
              <div class="flex items-center mr-3" v-for="item in group" :key="item.id">
                <div class="block flex items-center h-5">
                  <input
                    type="checkbox"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    :id="item.id"
                    :value="item.id"
                    v-model="modelators"
                  />
                </div>
                <div class="ml-2 text-sm">
                  <label :for="item.id" class="font-medium text-gray-700 select-none cursor-pointer">
                    {{ `${item.name}` }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end mt-5">
      <Button @click="create" color="primary" text="Create" />
    </div>
  </div>
</template>

<script>
import Input from "../../components/Elements/Input.vue";
import Button from "../../components/Elements/Button.vue";
import { inject, onMounted, ref, watch, computed } from "vue";
import { GET, POST } from "../../services/api";
import { isEmpty } from "../../services/validate";
import { useRoute, useRouter } from "vue-router";
import { formatDateToYYYYMMDD } from '../../services/formater'

export default {
  name: "createProjects",
  components: {
    Input,
    Button,
  },

  setup() {
    const alertProps = inject("alert");
    const data = ref({
      name: "",
      sprint: "",
      required_hours: "",
      client: "",
      coordenator: 0,
      teamleader: 0,
      manager: 0,
      clientId: 0,
      start: formatDateToYYYYMMDD(new Date()),
      finish: formatDateToYYYYMMDD(new Date()),
      sector: '',
      status: 0
    });
    const loader = inject("loading");
    const alert = inject("alert");
    const router = useRouter();
    const route = useRoute();
    const managers = ref([]);
    const coordenators = ref([]);
    const teamleaders = ref([]);
    const clients = ref([]);
    const status = ref([]);
    const allUsers = ref([]);
    const modelators = ref([]);
    const groupedUsers = computed(() => {
      const groups = {
        'modelers': allUsers.value.filter(el => el.role === 2),
        'managers': managers.value,
        'coordinators': coordenators.value,
        'team Leaders': teamleaders.value,
      };
      const sortedGroups = {};
      Object.keys(groups).sort().forEach(key => {
        sortedGroups[key] = groups[key];
      });
      return sortedGroups;
    });

    onMounted(async () => {
      loader.open();
      try {
        const res = await GET("users/active");
        status.value = await GET('status');
        allUsers.value = [...res].sort((a, b) => a.name.localeCompare(b.name));
        managers.value = res.filter((el) => el.role === 3).sort((a, b) => a.name.localeCompare(b.name));
        coordenators.value = res.filter((el) => el.role === 4).sort((a, b) => a.name.localeCompare(b.name));
        teamleaders.value = res.filter((el) => el.role === 6).sort((a, b) => a.name.localeCompare(b.name));
        clients.value = res.filter((el) => el.role === 5).sort((a, b) => a.name.localeCompare(b.name));
        
        loader.close();
      } catch (e) {
        loader.close();
        if (
          e.includes("Token") ||
          e.includes("t_dynamik") ||
          e == "Unauthorized"
        ) {
          localStorage.removeItem("t_dynamik");
          localStorage.removeItem("info_dynamik");
          router.push({ path: "/login" });
        }
        alert.open("Error", e, "danger");
      }
    });

    watch(() => data.value.clientId, (newClientId) => {
      const selectedClient = clients.value.find(c => c.id == newClientId);
      if (selectedClient && selectedClient.client_account) {
        data.value.client = selectedClient.client_account;
      }
    });

    const create = async () => {
      const obj = { name: data.value.name };
      const validate = isEmpty(obj);
      
      if (validate) {
        validate && alert.open("Atenção!", validate, "warning");
      } else {
        loader.open();
        try {
          const inputToHours = data.value.required_hours * 60;
          
          const { teamleaders, ...dataWithoutTeamleaders } = data.value;
          const payload = {
            ...dataWithoutTeamleaders,
            required_hours: inputToHours,
            manager: parseInt(data.value.manager),
            coordenator: parseInt(data.value.coordenator),
            teamleader: parseInt(data.value.teamleaders)
          };

          const resposta = await POST("projects", payload);
          
          const team = modelators.value.map((elemento) => {
            return {
              project: resposta.project.id,
              user: elemento,
            };
          });

          await POST("team", team);

          alert.open("Success!", `Project successfully created!`, "success");
          loader.close();
          router.push("/projects");
        } catch (e) {
          alert.open("Attention!", e, "danger");
          loader.close();
          if (
            e.includes("Token") ||
            e.includes("t_dynamik") ||
            e == "Unauthorized"
          ) {
            localStorage.removeItem("t_dynamik");
            localStorage.removeItem("info_dynamik");
            router.push({ path: "/login" });
          }
        }
      }
    };

    return {
      alertProps,
      data,
      create,
      route,
      coordenators,
      teamleaders,
      managers,
      modelators,
      clients,
      status,
      groupedUsers
    };
  },
};
</script>